import { useRouter } from 'expo-router';
import { useCallback } from 'react';
import { TAB_ROUTE_BY_HREF } from '../config/navigation';
import { useTabsNavigation } from '../contexts/TabsNavigationContext';

// Navigates the navigation chrome (Sidebar / MobileTabBar) to a destination.
// For (app) tabs it performs a state-preserving tab jump so the target tab
// keeps its nested stack (e.g. an open lead detail). Re-tapping the tab that
// is already focused resets the section to its root instead: JUMP_TO would
// preserve the nested stack, leaving a nested screen in place with no visible
// reaction. We use router.dismissTo so any nested screen (e.g. an open lead
// detail) is popped back to the section root; if the root isn't in the stack,
// dismissTo replaces the current screen with it. router.navigate did NOT reliably pop nested screens
// on web, which left users stranded. Anything that isn't an (app) tab — or a
// jump attempted before the Tabs navigation is ready — falls back to standard
// router navigation.
export function useNavigateToTab(): (href: string) => void {
  const router = useRouter();
  const tabsNav = useTabsNavigation();

  return useCallback(
    (href: string) => {
      const tabRouteName = TAB_ROUTE_BY_HREF[href];
      const isRetapOnFocusedTab = tabRouteName && tabsNav?.activeTabRouteName === tabRouteName;
      if (isRetapOnFocusedTab) {
        router.dismissTo(href as Parameters<typeof router.dismissTo>[0]);
        // Pulse so the section resets its scroll to top — dismissTo pops nested
        // screens but leaves the already-rooted list at its old offset.
        tabsNav?.signalTabNavigation(tabRouteName);
        return;
      }
      if (tabRouteName && tabsNav?.jumpToTab(tabRouteName)) {
        // JUMP_TO is state-preserving, so the target keeps its scroll offset;
        // pulse so the section resets to top on arrival (FLOSEAI-416).
        tabsNav.signalTabNavigation(tabRouteName);
        return;
      }
      router.navigate(href as Parameters<typeof router.navigate>[0]);
    },
    [router, tabsNav],
  );
}
